import React from 'react';
import { 
    Card, 
    Typography, 
    Switch, 
    IconButton, 
    Tooltip
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Refresh, Delete } from '@material-ui/icons';
import {
    CircularProgressbarWithChildren,
    buildStyles
} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { formatDate } from '../../../../helpers';
import { FormattedMessage } from 'react-intl';
import ellipsis from '../../../../assets/Loading/Ellipsis.svg';

const styles = (theme) => ({
    bookCard: {
        width: 368,
        height: 360,
        margin: theme.spacing(1),
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
    },
    cardHeader: {
        backgroundColor: '#0087B6',
        color: 'white',
        padding: theme.spacing(2),
        display: 'flex',
        alignItems: 'flex-start',
        minHeight: 120,
    },
    bookCover: {
        width: 90,
        height: 110,
        backgroundColor: 'transparent',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: theme.spacing(2),
        backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'white\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z\'/%3E%3C/svg%3E")',
        backgroundSize: '30px 30px',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        overflow: 'hidden',
    },
    bookInfo: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        paddingTop: theme.spacing(1),
    },
    bookTitle: {
        fontSize: '1.4rem',
        fontWeight: 700,
        marginBottom: theme.spacing(0.5),
        lineHeight: 1.3,
        color: '#ffffff',
        textShadow: '0 1px 3px rgba(0,0,0,0.4)',
        letterSpacing: '0.5px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        cursor: 'default',
    },
    bookAuthor: {
        fontSize: '0.85rem',
        opacity: 0.9,
        color: 'white',
    },
    cardBody: {
        backgroundColor: 'white',
        flex: 1,
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    mainControlsRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    sideControl: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 90,
        position: 'relative',
        overflow: 'visible',
    },
    controlLabel: {
        fontSize: '0.8rem',
        fontWeight: 500,
        color: '#333',
        marginBottom: theme.spacing(0.5),
        textAlign: 'center',
        whiteSpace: 'nowrap',
    },
    progressSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
    },
    progressContainer: {
        width: 70,
        height: 70,
        marginBottom: theme.spacing(1),
    },
    lastReadText: {
        fontSize: '0.75rem',
        color: '#666',
        textAlign: 'center',
    },
    cardFooter: {
        backgroundColor: '#f5f5f5',
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(1.5),
        gap: theme.spacing(6),
    },
    actionButton: {
        color: '#666',
        backgroundColor: '#e8e8e8',
        width: 40,
        height: 40,
        borderRadius: '50%',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            backgroundColor: '#d0d0d0',
            boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
            color: '#333',
            transform: 'translateY(-1px)',
        },
    },
    loading: {
        // Add loading styles if needed
    },
    largeTooltip: {
        fontSize: '14px',
        maxWidth: 300,
        padding: '8px 12px',
        lineHeight: 1.4,
    },
});

// Component to handle conditional tooltip for truncated text
const TruncatedTitle = ({ classes, title }) => {
    const titleRef = React.useRef(null);
    const [isTruncated, setIsTruncated] = React.useState(false);

    React.useEffect(() => {
        const element = titleRef.current;
        if (element) {
            // Check if text is truncated by comparing scroll height with client height
            setIsTruncated(element.scrollHeight > element.clientHeight);
        }
    }, [title]);

    const titleElement = (
        <Typography 
            ref={titleRef}
            className={classes.bookTitle}
        >
            {title}
        </Typography>
    );

    if (isTruncated) {
        return (
            <Tooltip 
                title={title} 
                placement="top"
                classes={{ tooltip: classes.largeTooltip }}
            >
                {titleElement}
            </Tooltip>
        );
    }

    return titleElement;
};

const BookCard = ({ 
    classes,
    value = {}, // Using 'value' to match SortableItem prop name, but it's the book data
    toggleType,
    toggleEcho,
    handleReset,
    handleDelete,
    isDashboardEnabled
}) => {
    const {
        name = "Unknown", // SortableItem uses 'name' for title
        author = "Unknown",
        titleImage,
        titleImageSrc,
        progress = 0,
        lastRead,
        wordRate = '0', // '0' for sentence, '75' for paragraph, 'Processing' for loading
        id,
        bookLevel,
        pendingRequestId
    } = value;

    const isPassageLengthOn = wordRate === '75';
    const isProcessing = wordRate === 'Processing';
    const progressValue = progress || 0;
    
    // Echo mode logic matches SortableItem: wordRate === '-1' means echo is on
    const isEchoModeOn = wordRate === '-1';
    const isPending = Boolean(pendingRequestId);

    return (
        <Card className={classes.bookCard}>
            {/* Header Section */}
            <div className={classes.cardHeader}>
                <div className={classes.bookCover}>
                    {(titleImage || titleImageSrc) && (
                        <img 
                            src={titleImage || titleImageSrc} 
                            alt={name}
                            style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'contain',
                                borderRadius: 8 
                            }}
                        />
                    )}
                </div>
                <div className={classes.bookInfo}>
                    <TruncatedTitle classes={classes} title={name} />
                    <Typography className={classes.bookAuthor}>
                        {author}
                    </Typography>
                    {isDashboardEnabled && bookLevel && (
                        <Typography className={classes.bookAuthor}>
                            {bookLevel}
                        </Typography>
                    )}
                </div>
            </div>

            {/* Body Section */}
            <div className={classes.cardBody}>
                <div className={classes.mainControlsRow}>
                    {/* Left Control - Passage Length */}
                    <div className={classes.sideControl}>
                        <Typography className={classes.controlLabel}>
                            Passage Length
                        </Typography>
                        <Tooltip title={wordRate === '0' ? 'Sentence' : 'Paragraph'}>
                            {isProcessing ? (
                                <img className={classes.loading} src={ellipsis} height={25} width={25} alt='loading...' />
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '12px', fontWeight: '600', color: !isPassageLengthOn ? '#3F51B5' : '#A1AADA' }}>
                                        S
                                    </span>
                                    <Switch
                                        checked={isPassageLengthOn}
                                        onChange={() => toggleType && toggleType(value)}
                                        name='status'
                                        size="medium"
                                        color="primary"
                                        aria-label='toggle Sentence or Paragraph'
                                        disabled={isPending}
                                    />
                                    <span style={{ fontSize: '12px', fontWeight: '600', color: isPassageLengthOn ? '#3F51B5' : '#A1AADA' }}>
                                        P
                                    </span>
                                </div>
                            )}
                        </Tooltip>
                    </div>

                    {/* Center - Progress Section */}
                    <div className={classes.progressSection}>
                        <div className={classes.progressContainer}>
                            <CircularProgressbarWithChildren
                                value={progressValue}
                                strokeWidth={8}
                                circleRatio={0.75}
                                styles={buildStyles({
                                    strokeLinecap: "butt",
                                    rotation: 1 / 2 + 1 / 8,
                                    textSize: '18px',
                                    textColor: progressValue === 100 ? '#5A9A5A' : progressValue === 0 ? '#CC8A50' : '#0087B6',
                                    pathColor: progressValue === 100 ? '#5A9A5A' : progressValue === 0 ? '#CC8A50' : '#0087B6',
                                    trailColor: '#e0e0e0',
                                })}
                            >
                                <Typography style={{ 
                                    fontSize: '14px', 
                                    fontWeight: 'bold', 
                                    color: progressValue === 100 ? '#5A9A5A' : progressValue === 0 ? '#CC8A50' : '#0087B6'
                                }}>
                                    {progressValue}%
                                </Typography>
                            </CircularProgressbarWithChildren>
                        </div>
                        <Typography className={classes.lastReadText}>
                            {lastRead ? `Last: ${formatDate(lastRead)}` : 'Last: never'}
                        </Typography>
                    </div>

                    {/* Right Control - Echo Mode */}
                    <div className={classes.sideControl}>
                        <Typography className={classes.controlLabel}>
                            Echo Mode
                        </Typography>
                        <Tooltip title={wordRate === '75' ? "Turning on Echo Mode will automatically switch to Sentence mode." : "Repeat passages with the computer instead of alternating. Recommended for young readers."}>
                            <Switch
                                checked={isEchoModeOn}
                                onChange={() => toggleEcho && toggleEcho(value)}
                                name='status'
                                size="medium"
                                color="primary"
                                aria-label='toggle Echo Mode'
                                disabled={isPending}
                            />
                        </Tooltip>
                    </div>
                </div>
            </div>

            {/* Footer Section */}
            <div className={classes.cardFooter}>
                <Tooltip title='Reset Progress'>
                    <IconButton 
                        className={classes.actionButton}
                        onClick={() => handleReset && handleReset(value)}
                        size="small"
                        aria-label='reset scores'
                    >
                        <Refresh />
                    </IconButton>
                </Tooltip>
                <Tooltip title={<FormattedMessage id='General.delete' />}>
                    <IconButton 
                        className={classes.actionButton}
                        onClick={() => handleDelete && handleDelete(value)}
                        size="small"
                        aria-label='delete'
                    >
                        <Delete />
                    </IconButton>
                </Tooltip>
            </div>
        </Card>
    );
};

export default withStyles(styles)(BookCard);
